/**
 * Builder v2 postMessage protocol (spec §5.3).
 *
 * Single source of truth for the shell ↔ bridge contract. Same-origin, yet
 * strictly versioned: every message travels in a `{ pb: 1, type, payload }`
 * envelope and both sides drop anything that fails `isPbMessage`.
 *
 * Phase 1 ships the read-only subset; drag/patch/inline-edit messages are
 * added to the unions in later phases.
 */

export const PB_PROTOCOL_VERSION = 1;

export interface BlockRect {
    top: number;
    left: number;
    width: number;
    height: number;
}

/** Sanitization profile for inline commits (spec §5.6). */
export type InlineMode = 'plain' | 'headline' | 'richtext';

export interface InlineAttr {
    /** CSS selector inside the rendered block, or null for its root element. */
    selector: string | null;
    mode: InlineMode;
}

export interface InitPayload {
    options: {
        /** Block link navigation inside the preview document. */
        blockLinks: boolean;
    };
    /** Hover label per block id (type name resolved by the shell). */
    labels: Record<string, string>;
    /** Inline-editable attributes per block id (types resolved by the shell). */
    inline?: Record<string, Record<string, InlineAttr>>;
    /** Blocks with a table-matrix attribute — enables cell editing (§table). */
    tables?: Record<string, { attr: string }>;
    /** Parent theme accent for overlay chrome; optional — an older cached
     *  bridge build simply keeps its built-in default. */
    accent?: string;
}

/**
 * A resolved drop position. `beforeId` is the rendered sibling the dragged
 * content lands before (null = append at the end of the slot) — sibling ids
 * are stable across hidden/other-domain blocks, a numeric index is not.
 */
export interface DropTarget {
    /** Container block id, or null for the content region root. */
    parentId: string | null;
    /** Slot index inside the parent (0 when parentId is null). */
    slot: number;
    /** Rendered sibling to insert before, or null to append. */
    beforeId: string | null;
}

/**
 * Drop targets valid for the current drag, computed by the shell from the
 * nesting rules — the bridge stays purely geometric.
 */
export interface AllowedDropTargets {
    /** Whether dropping at the content region root is allowed. */
    root: boolean;
    /** Allowed container parents with their open slot indexes. */
    parents: Array<{ id: string; slots: number[] }>;
}

export type ParentToBridgeMessage =
    | { type: 'init'; payload: InitPayload }
    | { type: 'select'; payload: { id: string | null } }
    | { type: 'hover'; payload: { id: string | null } }
    | { type: 'scrollTo'; payload: { id: string } }
    | { type: 'patchLevel'; payload: { id: string; html: string } }
    | { type: 'patchRegion'; payload: { html: string } }
    | { type: 'dragStart'; payload: { kind: 'new' | 'move'; blockIds: string[]; allowed: AllowedDropTargets } }
    | { type: 'dragMove'; payload: { x: number; y: number } }
    | { type: 'dragEnd'; payload: Record<string, never> }
    | { type: 'dragCancel'; payload: Record<string, never> };

export type BridgeToParentMessage =
    | { type: 'ready'; payload: { blockIds: string[] } }
    | { type: 'blockClick'; payload: { id: string } }
    | { type: 'blockDblClick'; payload: { id: string } }
    | { type: 'blockContextMenu'; payload: { id: string; x: number; y: number; cell: { row: number; col: number } | null } }
    | { type: 'pageContextMenu'; payload: { x: number; y: number } }
    | { type: 'blockHover'; payload: { id: string | null } }
    | { type: 'rects'; payload: { rects: Record<string, BlockRect>; documentHeight: number } }
    | { type: 'patched'; payload: { ok: boolean; blockIds: string[]; scriptErrors: string[] } }
    | { type: 'patchScriptError'; payload: { message: string } }
    | { type: 'navigateBlocked'; payload: { url: string } }
    | { type: 'dropTarget'; payload: { target: DropTarget | null } }
    | { type: 'drop'; payload: { target: DropTarget | null } }
    | { type: 'inlineEditStart'; payload: { id: string; attr: string } }
    | { type: 'inlineEditCommit'; payload: { id: string; attr: string; html: string; final: boolean } }
    | { type: 'inlineEditCancel'; payload: { id: string; attr: string } }
    | { type: 'tableCellEditStart'; payload: { id: string; attr: string; row: number; col: number } }
    | { type: 'tableCellCommit'; payload: { id: string; attr: string; row: number; col: number; text: string; final: boolean } }
    | { type: 'tableCellEditCancel'; payload: { id: string; attr: string } }
    | { type: 'shortcut'; payload: { name: 'palette' | 'keymap' } };

export type PbMessage = ParentToBridgeMessage | BridgeToParentMessage;

export type PbEnvelope<T extends PbMessage = PbMessage> = { pb: typeof PB_PROTOCOL_VERSION } & T;

export function envelope<T extends PbMessage>(message: T): PbEnvelope<T> {
    return { pb: PB_PROTOCOL_VERSION, ...message };
}

export function isPbMessage(data: unknown): data is PbEnvelope {
    if (typeof data !== 'object' || data === null) {
        return false;
    }

    const candidate = data as { pb?: unknown; type?: unknown };

    return candidate.pb === PB_PROTOCOL_VERSION && typeof candidate.type === 'string';
}
